import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Check, Inbox, Users, PhoneCall } from "lucide-react";
import { api, useAuth, formatApiError } from "@/App";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/enquiries");
      setEnquiries(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markContacted = async (id) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status: "contacted" });
      setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status: "contacted" } : e)));
      toast.success("Marked as contacted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/enquiries/${id}`);
      setEnquiries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Enquiry deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const newCount = enquiries.filter((e) => e.status === "new").length;

  const stats = [
    { label: "Total Enquiries", value: enquiries.length, icon: Inbox, id: "total" },
    { label: "New / Pending", value: newCount, icon: Users, id: "new" },
    { label: "Contacted", value: enquiries.length - newCount, icon: PhoneCall, id: "contacted" },
  ];

  return (
    <div className="min-h-screen bg-[#031435] text-[#F4F4F0]" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#081B40]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center" data-testid="dashboard-logo" aria-label="Edvio home">
              <img src="/edvio-logo-wide.png" alt="Edvio Language Institute" className="h-9 w-auto" />
            </Link>
            <span className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hidden sm:block">Enquiries Console</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/reviews/sync");
                  toast.success(data.added ? `${data.added} new Google review${data.added > 1 ? "s" : ""} added` : "Reviews are up to date");
                } catch (e) { toast.error("Review sync failed"); }
              }}
              className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] border border-white/15 rounded-full px-4 py-2 hover:border-[#C44900] hover:text-[#C44900] transition-colors duration-300"
              data-testid="sync-reviews-button"
            >
              <RefreshCw size={13} /> Sync Reviews
            </button>
            <span className="text-xs text-[#A3A3A3] hidden md:block" data-testid="dashboard-user-email">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] border border-white/15 rounded-full px-4 py-2 hover:border-[#C44900] hover:text-[#C44900] transition-colors duration-300"
              data-testid="admin-logout-button"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10 lg:py-14">
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          {stats.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="bg-[#081B40] border border-white/10 rounded-lg p-6 flex items-center justify-between"
              data-testid={`stat-card-${s.id}`}
            >
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">{s.label}</p>
                <p className="mt-2 font-display text-4xl tracking-tight">{s.value}</p>
              </div>
              <s.icon size={22} className="text-[#C44900]" />
            </motion.div>
          ))}
        </div>

        <div className="flex items-center justify-between mb-5">
          <h1 className="font-display text-2xl tracking-tight">Recent Enquiries</h1>
          <button
            onClick={load}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#A3A3A3] hover:text-[#F4F4F0] transition-colors"
            data-testid="refresh-enquiries-button"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <div className="bg-[#081B40] border border-white/10 rounded-lg overflow-hidden" data-testid="enquiries-table-container">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-[#A3A3A3] text-xs uppercase tracking-[0.15em]">Student</TableHead>
                <TableHead className="text-[#A3A3A3] text-xs uppercase tracking-[0.15em]">Contact</TableHead>
                <TableHead className="text-[#A3A3A3] text-xs uppercase tracking-[0.15em]">Course</TableHead>
                <TableHead className="text-[#A3A3A3] text-xs uppercase tracking-[0.15em] hidden lg:table-cell">Message</TableHead>
                <TableHead className="text-[#A3A3A3] text-xs uppercase tracking-[0.15em]">Status</TableHead>
                <TableHead className="text-[#A3A3A3] text-xs uppercase tracking-[0.15em] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {enquiries.length === 0 && !loading && (
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableCell colSpan={6} className="text-center py-16 text-[#A3A3A3] text-sm" data-testid="enquiries-empty-state">
                    No enquiries yet. New submissions from the website will appear here.
                  </TableCell>
                </TableRow>
              )}
              {enquiries.map((e) => (
                <TableRow key={e.id} className="border-white/10 hover:bg-white/[0.03]" data-testid={`enquiry-row-${e.id}`}>
                  <TableCell>
                    <p className="text-sm font-medium text-[#F4F4F0]">{e.name}</p>
                    <p className="text-xs text-[#A3A3A3] mt-0.5">{e.created_at ? new Date(e.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}</p>
                  </TableCell>
                  <TableCell>
                    <a href={`tel:${e.phone}`} className="text-sm text-[#F4F4F0]/90 hover:text-[#C44900] transition-colors block">{e.phone}</a>
                    {e.email && <span className="text-xs text-[#A3A3A3] block mt-0.5">{e.email}</span>}
                  </TableCell>
                  <TableCell className="text-sm">{e.course}</TableCell>
                  <TableCell className="text-sm text-[#A3A3A3] max-w-[240px] truncate hidden lg:table-cell">{e.message || "—"}</TableCell>
                  <TableCell>
                    <span
                      className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full border ${
                        e.status === "new" ? "border-[#C44900]/50 text-[#C44900]" : "border-emerald-500/40 text-emerald-400"
                      }`}
                      data-testid={`enquiry-status-${e.id}`}
                    >
                      {e.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {e.status === "new" && (
                        <button
                          onClick={() => markContacted(e.id)}
                          className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-[#A3A3A3] hover:border-emerald-500 hover:text-emerald-400 transition-colors"
                          title="Mark contacted"
                          data-testid={`enquiry-contact-button-${e.id}`}
                        >
                          <Check size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => remove(e.id)}
                        className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-[#A3A3A3] hover:border-red-500 hover:text-red-400 transition-colors"
                        title="Delete"
                        data-testid={`enquiry-delete-button-${e.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
}
